import { useEffect, useRef, useState } from "react"

const IS_SERVER = typeof window === 'undefined'

type IntersectionObserverOptions = {
  threshold: number | number[]
  onChance?: (isIntersecting: boolean, entry: IntersectionObserverEntry) => void
}

const useInterceptionObserver = (props: IntersectionObserverOptions) => {
  const { threshold, onChance } = props
  const [target, setTarget] = useState<Element | null>(null)
  const [visible, setVisible] = useState(false)

  const onChangeRef = useRef<IntersectionObserverOptions['onChance']>()
  onChangeRef.current = onChance

  useEffect(() => {
    if (IS_SERVER) return

    if (!('IntersectionObserver' in window)) return

    if (!target) return

    const thresholds = Array.isArray(threshold) ? threshold : [threshold]
    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]): void => {
        const [current] = entries
        const intersects = current.isIntersecting
          && thresholds.some(threshold => current.intersectionRatio >= threshold)

        setVisible(intersects)

        if (onChangeRef.current) {
          onChangeRef.current(intersects, current)
        }
      },
      { threshold: thresholds }
    )

    observer.observe(target)

    return () => {
      observer.unobserve(target)
    }
  }, [threshold, target])

  return [visible, setTarget] as const
}

export default useInterceptionObserver
export { useInterceptionObserver } // Exporting useInterceptionObserver for named import